/**
 * src/components/spatial/useDraggablePanel.ts
 *
 * Tiny, dependency-free hook that makes a floating map panel draggable.
 *
 * Usage:
 *   const { containerRef, containerStyle, dragHandleProps } =
 *     useDraggablePanel({ top: 80, right: 16 });
 *   return (
 *     <div ref={containerRef} style={{ ...containerStyle, ...panelStyle }}>
 *       <div {...dragHandleProps}>⠿ drag me</div>
 *       …
 *     </div>
 *   );
 *
 * The panel starts at the supplied corner anchor (top/left/right/bottom).
 * The first drag converts that to an absolute left/top (computed from the
 * live bounding box so there's no visual jump) and then follows the
 * pointer, clamped inside the panel's positioned ancestor (the map
 * container). Pointer capture via window listeners keeps the drag smooth
 * even when the cursor outruns the element.
 */

import { useCallback, useRef, useState } from 'react';
import type React from 'react';

export interface DraggableAnchor {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

export function useDraggablePanel(initial: DraggableAnchor, zIndex = 6) {
  const [anchor, setAnchor] = useState<DraggableAnchor>(initial);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const el = containerRef.current;
    const d = dragRef.current;
    if (!el || !d) return;
    const parent = (el.offsetParent as HTMLElement) || document.body;
    const pr = parent.getBoundingClientRect();
    let left = e.clientX - pr.left - d.dx;
    let top = e.clientY - pr.top - d.dy;
    const maxLeft = Math.max(0, pr.width - el.offsetWidth);
    const maxTop = Math.max(0, pr.height - el.offsetHeight);
    left = Math.max(0, Math.min(left, maxLeft));
    top = Math.max(0, Math.min(top, maxTop));
    // Switch to pure left/top anchoring (drop right/bottom) once dragging.
    setAnchor({ left, top });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      // Ignore drags that start on an interactive control inside the handle.
      const target = e.target as HTMLElement;
      if (target.closest('input,select,textarea,button[data-no-drag]')) return;
      const rect = el.getBoundingClientRect();
      dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      e.preventDefault();
      e.stopPropagation();
    },
    [onPointerMove, onPointerUp],
  );

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex,
    ...anchor,
  };

  return {
    containerRef,
    containerStyle,
    dragHandleProps: { onPointerDown, style: { cursor: 'move', touchAction: 'none' as const } },
  };
}
